import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { forgotPassword, resetPasswordWithOtp } from "@/lib/api";
import { ArrowRight, Loader2, Mail, Lock, KeyRound, Eye, EyeOff, ShieldCheck } from "lucide-react";
import ScoreRing from "@/components/wishire/ScoreRing";
import WishireLogo from "@/components/wishire/WishireLogo";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [step, setStep] = useState("request"); // request | reset
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  if (user) {
    const dest = user.is_admin ? "/admin" : user.role === "company" ? "/company" : user.role === "agency" ? "/agency" : "/app";
    return <Navigate to={dest} replace />;
  }

  const requestCode = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await forgotPassword(email.trim().toLowerCase());
      setNotice(data?.message || "If an account exists for that email, a reset code is on its way.");
      setStep("reset");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send a reset code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await resetPasswordWithOtp({
        email: email.trim().toLowerCase(),
        otp: otp.trim(),
        new_password: newPassword,
      });
      const fresh = await refresh();
      const u = fresh || data.user;
      const dest = u?.is_admin ? "/admin" : u?.role === "company" ? "/company" : u?.role === "agency" ? "/agency" : "/app";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not reset your password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 wsh-grain bg-[#FDFBF7]">
      <div className="p-10 md:p-16 flex flex-col">
        <button
          onClick={() => navigate("/")}
          className="self-start font-display text-2xl font-bold inline-flex items-baseline gap-1"
          data-testid="forgot-back-home"
        >
          <WishireLogo size="lg" />
        </button>

        <div className="flex-1 flex flex-col justify-center max-w-md w-full">
          <span className="tag">
            <KeyRound size={12} /> {step === "request" ? "Reset password" : "Enter your code"}
          </span>
          <h1 className="font-display mt-6 text-4xl md:text-5xl leading-[1.05]">
            {step === "request" ? (
              <>Forgot your <span className="italic text-stone-500">password?</span></>
            ) : (
              <>Check your <span className="italic text-stone-500">inbox.</span></>
            )}
          </h1>

          {step === "request" ? (
            <form onSubmit={requestCode} className="mt-8 space-y-4" data-testid="forgot-request-form">
              <p className="text-sm text-stone-600 leading-relaxed">
                Enter the email tied to your account and we&apos;ll send you a one-time code to reset your password.
              </p>
              <div>
                <label className="overline">Email</label>
                <div className="mt-2 relative">
                  <Mail size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-500" />
                  <input
                    type="email"
                    required
                    data-testid="forgot-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="w-full pl-12 pr-4 py-3.5 rounded-full bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm"
                  />
                </div>
              </div>

              {error && <p className="text-red-700 text-sm" data-testid="forgot-error">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit"
                className="inline-flex items-center justify-center gap-3 w-full px-5 py-3.5 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                <span className="font-medium">{loading ? "Sending…" : "Send reset code"}</span>
                {!loading && <ArrowRight size={16} />}
              </button>
            </form>
          ) : (
            <form onSubmit={resetPassword} className="mt-8 space-y-4" data-testid="forgot-reset-form">
              {notice && (
                <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-start gap-2" data-testid="forgot-notice">
                  <ShieldCheck size={16} className="mt-0.5 shrink-0" /> {notice}
                </p>
              )}
              <div>
                <label className="overline">6-digit code</label>
                <div className="mt-2 relative">
                  <KeyRound size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-500" />
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    data-testid="forgot-otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    className="w-full pl-12 pr-4 py-3.5 rounded-full bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm font-mono tracking-[0.3em]"
                  />
                </div>
              </div>
              <div>
                <label className="overline">New password (min 8 chars)</label>
                <div className="mt-2 relative">
                  <Lock size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-500" />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    data-testid="forgot-new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-12 pr-12 py-3.5 rounded-full bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    data-testid="forgot-password-toggle"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    title={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700 transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {error && <p className="text-red-700 text-sm" data-testid="forgot-reset-error">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-reset-submit"
                className="inline-flex items-center justify-center gap-3 w-full px-5 py-3.5 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                <span className="font-medium">{loading ? "Resetting…" : "Reset password & sign in"}</span>
                {!loading && <ArrowRight size={16} />}
              </button>

              <button
                type="button"
                onClick={() => { setStep("request"); setError(null); setNotice(null); setOtp(""); }}
                className="text-sm text-stone-600 hover:text-stone-900 underline underline-offset-4"
              >
                Use a different email
              </button>
            </form>
          )}

          <p className="mt-6 text-sm text-stone-600">
            Remembered it?{" "}
            <button
              type="button"
              onClick={() => navigate("/login")}
              data-testid="forgot-login-link"
              className="font-medium text-stone-900 underline underline-offset-4 hover:text-[#C2410C]"
            >
              Back to sign in
            </button>
          </p>
        </div>
      </div>

      <div className="hidden md:flex items-center justify-center relative overflow-hidden bg-stone-900 text-stone-50">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(700px 400px at 80% 30%, rgba(217,119,6,0.4), transparent 60%), radial-gradient(500px 400px at 10% 80%, rgba(21,128,61,0.3), transparent 60%)",
          }}
        />
        <div className="relative text-center px-10">
          <ScoreRing value={86} size={300} dark />
          <h2 className="font-display text-3xl mt-10 max-w-md">
            Back in{" "}
            <span className="italic text-amber-300">a couple of taps.</span>
          </h2>
          <p className="mt-4 text-stone-300 max-w-sm mx-auto leading-relaxed">
            We&apos;ll email you a secure one-time code. Enter it, pick a new
            password, and you&apos;re straight back into your workspace.
          </p>
        </div>
      </div>
    </div>
  );
}
